import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { bdCell, labelOf, splitRefs } from './bd-defs.js';
import { toneOf } from './bd-presentation.js';
import type { BdArtifactDef, BdColumnDef, BdRow } from '../../../lib/api/basic-design.js';

export interface BdRowsViewProps {
  /** 節の定義（列の見出し・選択肢の表示名） */
  def: BdArtifactDef;
  /** 保存済みの行 */
  rows: readonly BdRow[];
  /** 見ている人が隠した列（個人の見え方） */
  hiddenColumns?: readonly string[];
}

/**
 * 節の行を読む。1 行 1 枚のカードで、見出しに行 ID・選ぶ欄の表示名・最初の文章の欄、
 * 本文にほかの欄を見出しつきで並べる。参照とフローのノードは 1 つずつの札にする。
 *
 * 節ごとに専用の見た目を作ると、節が増えるたびに画面を直すことになる。影響一覧と
 * リスク一覧（読み手が絞り込んで読むもの）以外は、定義だけで描けるこの形にする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の行を、定義の見出し・選択肢の表示名・参照の札つきのカードで読む
 * @graph-connects BdRowCard [calls] 1 行のカード
 */
export function BdRowsView({ def, rows, hiddenColumns = [] }: BdRowsViewProps) {
  const { t } = useTranslation();
  const shown = { ...def, columns: def.columns.filter((c) => !hiddenColumns.includes(c.key)) };
  return (
    <ol className="space-y-3" aria-label={t('bd.view.rowsAria', { title: def.title })}>
      {rows.map((row, index) => (
        // 同じ中身の行が並ぶことがあるので、保存済みの並びの位置で鍵を作る
        <li key={index}>
          <BdRowCard def={shown} row={row} number={index + 1} />
        </li>
      ))}
    </ol>
  );
}

interface BdRowCardProps {
  def: BdArtifactDef;
  row: BdRow;
  /** 保存済みの並びでの行番号（1 から） */
  number: number;
}

/** 札で並べる欄か。 @graph-connects none */
function isTokenColumn(column: BdColumnDef): boolean {
  return column.kind === 'ref' || column.kind === 'refs' || column.kind === 'flowNodes';
}

/**
 * 1 行のカード。
 * @graph-connects splitRefs [calls] 参照の分割
 * @graph-connects labelOf [calls] 選択肢の表示名
 */
function BdRowCard({ def, row, number }: BdRowCardProps) {
  const id = def.columns.some((c) => c.kind === 'id') ? bdCell(row, 'id') : '';
  const choices = def.columns.filter((c) => c.kind === 'choice' && bdCell(row, c.key) !== '');
  const title = def.columns.find((c) => c.kind === 'text' && bdCell(row, c.key).trim() !== '');
  const rest = def.columns.filter(
    (c) =>
      c.kind !== 'id' &&
      c.kind !== 'choice' &&
      c.key !== title?.key &&
      bdCell(row, c.key).trim() !== '',
  );

  return (
    <Card padding="sm">
      <CardContent className="mt-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs tabular-nums text-[var(--color-text-muted)]">#{number}</span>
          {id === '' ? null : (
            <code className="rounded bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-xs font-semibold text-[var(--color-text-primary)]">
              {id}
            </code>
          )}
          {choices.map((column) => (
            <Badge
              key={column.key}
              variant={toneOf(column.key, bdCell(row, column.key))}
              title={column.label}
            >
              {labelOf(column, bdCell(row, column.key))}
            </Badge>
          ))}
          {title === undefined ? null : (
            <h3 className="min-w-0 flex-1 text-sm font-semibold text-[var(--color-text-primary)]">
              {bdCell(row, title.key)}
            </h3>
          )}
        </div>

        {rest.length === 0 ? null : (
          <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 border-t border-[var(--color-border)] pt-3 text-sm md:grid-cols-[10rem_1fr]">
            {rest.map((column) => (
              <div key={column.key} className="contents">
                <dt className="text-xs text-[var(--color-text-muted)]">{column.label}</dt>
                <dd className="min-w-0">
                  {isTokenColumn(column) ? (
                    <ul className="flex flex-wrap gap-1" aria-label={column.label}>
                      {splitRefs(bdCell(row, column.key)).map((token, i) => (
                        // 同じ ID を 2 度書いた行もそのまま出すので、位置で鍵を作る
                        <li key={`${String(i)}:${token}`}>
                          <code className="rounded bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-xs text-[var(--color-text-secondary)]">
                            {token}
                          </code>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="whitespace-pre-wrap break-words text-[var(--color-text-primary)]">
                      {bdCell(row, column.key)}
                    </p>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
